import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { gymnasiumService } from '../../services/gymnasiumService';
import { FiPlus, FiTrash2, FiSave, FiX } from 'react-icons/fi';

interface Scenario {
  label: string;
  prompt: string;
  criteria: string[];
  category: string;
  difficulty: string;
  max_score: number;
}

interface Props {
  onSaved: () => void;
  onCancel: () => void;
  editGauntlet?: any; // pass existing gauntlet for edit mode
}

const EMPTY_SCENARIO: Scenario = {
  label: '', prompt: '', criteria: [''], category: '', difficulty: 'medium', max_score: 10,
};

const GauntletEditor: React.FC<Props> = ({ onSaved, onCancel, editGauntlet }) => {
  const { darkMode } = useTheme();
  const [name, setName] = useState(editGauntlet?.name || '');
  const [description, setDescription] = useState(editGauntlet?.description || '');
  const [tags, setTags] = useState(editGauntlet?.tags?.join(', ') || '');
  const [scenarios, setScenarios] = useState<Scenario[]>(
    editGauntlet?.scenarios?.map((s: any) => ({
      label: s.label, prompt: s.prompt, criteria: s.criteria || [''],
      category: s.category || '', difficulty: s.difficulty || 'medium', max_score: s.max_score || 10,
    })) || [{ ...EMPTY_SCENARIO }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
  } border focus:outline-none focus:ring-2 focus:ring-amber-500/30`;

  const updateScenario = (i: number, field: keyof Scenario, value: any) => {
    setScenarios(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  };

  const addCriterion = (i: number) => {
    setScenarios(prev => prev.map((s, idx) => idx === i ? { ...s, criteria: [...s.criteria, ''] } : s));
  };

  const updateCriterion = (si: number, ci: number, value: string) => {
    setScenarios(prev => prev.map((s, idx) =>
      idx === si ? { ...s, criteria: s.criteria.map((c, j) => j === ci ? value : c) } : s
    ));
  };

  const removeCriterion = (si: number, ci: number) => {
    setScenarios(prev => prev.map((s, idx) =>
      idx === si ? { ...s, criteria: s.criteria.filter((_, j) => j !== ci) } : s
    ));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (scenarios.length === 0) { setError('At least one scenario is required'); return; }
    if (scenarios.some(s => !s.label.trim() || !s.prompt.trim())) {
      setError('All scenarios need a label and prompt'); return;
    }

    setSaving(true);
    setError('');
    try {
      const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        name: name.replace(/\s+/g, '_'),
        description,
        tags: tagList,
        scenarios: scenarios.map(s => ({
          ...s,
          criteria: s.criteria.filter(c => c.trim()),
        })),
      };

      if (editGauntlet) {
        await gymnasiumService.updateGauntlet(editGauntlet.id, payload);
      } else {
        await gymnasiumService.createGauntlet(payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
          {editGauntlet ? 'Edit Gauntlet' : 'Create Gauntlet'}
        </h1>
        <button onClick={onCancel} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <FiX className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm">{error}</div>
      )}

      {/* Metadata */}
      <div className={sectionClass}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</label>
            <input className={inputClass} value={name} onChange={e => setName(e.target.value)} placeholder="Backend_Architecture_Interview" />
          </div>
          <div>
            <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tags (comma-separated)</label>
            <input className={inputClass} value={tags} onChange={e => setTags(e.target.value)} placeholder="architecture, backend, senior" />
          </div>
        </div>
        <div className="mt-4">
          <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
          <textarea className={`${inputClass} h-20`} value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this gauntlet test?" />
        </div>
      </div>

      {/* Scenarios */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Scenarios ({scenarios.length})
          </h2>
          <button
            onClick={() => setScenarios([...scenarios, { ...EMPTY_SCENARIO }])}
            className="text-xs flex items-center gap-1 text-amber-400 hover:text-amber-300"
          >
            <FiPlus className="w-3 h-3" /> Add Scenario
          </button>
        </div>

        {scenarios.map((s, i) => (
          <div key={i} className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Scenario {i + 1}</span>
              {scenarios.length > 1 && (
                <button onClick={() => setScenarios(scenarios.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <input className={inputClass} value={s.label} onChange={e => updateScenario(i, 'label', e.target.value)} placeholder="Scenario label" />
              <input className={inputClass} value={s.category} onChange={e => updateScenario(i, 'category', e.target.value)} placeholder="Category (e.g. system_design)" />
              <select className={inputClass} value={s.difficulty} onChange={e => updateScenario(i, 'difficulty', e.target.value)}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <textarea
              className={`${inputClass} h-24 mb-3`}
              value={s.prompt}
              onChange={e => updateScenario(i, 'prompt', e.target.value)}
              placeholder="The prompt that will be sent to the persona..."
            />
            <div>
              <label className={`text-[10px] font-medium mb-1 block ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CRITERIA</label>
              {s.criteria.map((c, ci) => (
                <div key={ci} className="flex gap-2 mb-1.5">
                  <input
                    className={`${inputClass} flex-1`}
                    value={c}
                    onChange={e => updateCriterion(i, ci, e.target.value)}
                    placeholder={`Criterion ${ci + 1}...`}
                  />
                  {s.criteria.length > 1 && (
                    <button onClick={() => removeCriterion(i, ci)} className="text-red-400 hover:text-red-300 shrink-0">
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {s.criteria.length < 10 && (
                <button onClick={() => addCriterion(i)} className="text-xs text-amber-400 hover:text-amber-300 mt-1">
                  + Add criterion
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all flex items-center gap-2 disabled:opacity-50"
        >
          <FiSave className="w-4 h-4" />
          {saving ? 'Saving...' : editGauntlet ? 'Update Gauntlet' : 'Create Gauntlet'}
        </button>
      </div>
    </div>
  );
};

export default GauntletEditor;
